import { MAX_FOOD_PHOTO_BYTES } from "./types";

const DEFAULT_MAX_DIMENSION = 1_600;
const DEFAULT_INITIAL_QUALITY = 0.86;
const DEFAULT_MINIMUM_QUALITY = 0.45;
const DEFAULT_MAX_SOURCE_BYTES = 32 * 1024 * 1024;
const QUALITY_SEARCH_STEPS = 6;
const RESIZE_ATTEMPTS = 8;

export type FoodPhotoSource = File | HTMLCanvasElement;
export type FoodPhotoPreparationErrorCode =
  | "invalid-image"
  | "source-too-large"
  | "decode-failed"
  | "canvas-unavailable"
  | "encode-failed"
  | "output-too-large";

export interface FoodPhotoOptions {
  /** Largest output edge in CSS pixels. */
  maxDimension?: number;
  /** Maximum decoded JPEG byte length, not the larger base64 string length. */
  maxBytes?: number;
  initialQuality?: number;
  minimumQuality?: number;
  maxSourceBytes?: number;
}

export class FoodPhotoPreparationError extends Error {
  constructor(message: string, readonly code: FoodPhotoPreparationErrorCode) {
    super(message);
    this.name = "FoodPhotoPreparationError";
  }
}

/**
 * Re-encodes a camera frame or uploaded image as a reasonably sized JPEG data
 * URL. createImageBitmap's explicit `from-image` option applies EXIF rotation;
 * the HTMLImageElement fallback uses the browser's normal oriented rendering.
 */
export async function prepareFoodPhoto(
  source: FoodPhotoSource,
  options: FoodPhotoOptions = {},
): Promise<string> {
  const configuration = normalizeOptions(options);
  const decoded = isFile(source)
    ? await decodeFile(source, configuration.maxSourceBytes)
    : canvasSource(source);

  try {
    return encodeCappedJpeg(decoded, configuration);
  } finally {
    decoded.dispose();
  }
}

export function foodPhotoDataUrlByteLength(dataUrl: string): number {
  const separator = dataUrl.indexOf(",");
  if (separator < 0 || !/;base64$/i.test(dataUrl.slice(0, separator))) {
    throw new FoodPhotoPreparationError("The encoded photo is not a base64 data URL", "encode-failed");
  }
  const base64 = dataUrl.slice(separator + 1);
  if (base64.length === 0 || base64.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) {
    throw new FoodPhotoPreparationError("The encoded photo contains invalid base64 data", "encode-failed");
  }
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return (base64.length / 4) * 3 - padding;
}

interface NormalizedFoodPhotoOptions {
  maxDimension: number;
  maxBytes: number;
  initialQuality: number;
  minimumQuality: number;
  maxSourceBytes: number;
}

interface DecodedPhoto {
  source: CanvasImageSource;
  width: number;
  height: number;
  dispose(): void;
}

function normalizeOptions(options: FoodPhotoOptions): NormalizedFoodPhotoOptions {
  const maxDimension = positiveInteger(options.maxDimension ?? DEFAULT_MAX_DIMENSION, "Maximum photo dimension");
  const maxBytes = positiveInteger(options.maxBytes ?? MAX_FOOD_PHOTO_BYTES, "Maximum photo byte length");
  const maxSourceBytes = positiveInteger(options.maxSourceBytes ?? DEFAULT_MAX_SOURCE_BYTES, "Maximum source byte length");
  const initialQuality = quality(options.initialQuality ?? DEFAULT_INITIAL_QUALITY, "Initial JPEG quality");
  const minimumQuality = quality(options.minimumQuality ?? DEFAULT_MINIMUM_QUALITY, "Minimum JPEG quality");
  if (minimumQuality > initialQuality) {
    throw new TypeError("Minimum JPEG quality cannot exceed initial JPEG quality");
  }
  return { maxDimension, maxBytes, maxSourceBytes, initialQuality, minimumQuality };
}

function positiveInteger(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value <= 0) throw new TypeError(`${label} must be a positive integer`);
  return value;
}

function quality(value: number, label: string): number {
  if (!Number.isFinite(value) || value <= 0 || value > 1) throw new TypeError(`${label} must be greater than 0 and at most 1`);
  return value;
}

function isFile(source: FoodPhotoSource): source is File {
  return typeof File !== "undefined" && source instanceof File;
}

function canvasSource(canvas: HTMLCanvasElement): DecodedPhoto {
  const width = validDimension(canvas.width);
  const height = validDimension(canvas.height);
  if (!width || !height || typeof canvas.getContext !== "function" || typeof canvas.toDataURL !== "function") {
    throw new FoodPhotoPreparationError("The captured camera frame is empty", "invalid-image");
  }
  return { source: canvas, width, height, dispose: () => undefined };
}

async function decodeFile(file: File, maxSourceBytes: number): Promise<DecodedPhoto> {
  if (file.size === 0 || (file.type && !file.type.toLocaleLowerCase().startsWith("image/"))) {
    throw new FoodPhotoPreparationError("Choose a valid image file", "invalid-image");
  }
  if (file.size > maxSourceBytes) {
    throw new FoodPhotoPreparationError("The original photo is too large to process safely", "source-too-large");
  }

  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      const width = validDimension(bitmap.width);
      const height = validDimension(bitmap.height);
      if (width && height) {
        return { source: bitmap, width, height, dispose: () => bitmap.close() };
      }
      bitmap.close();
    } catch {
      // Safari versions without full createImageBitmap support can still decode
      // camera files through an HTML image, which also honors EXIF orientation.
    }
  }
  return decodeWithImageElement(file);
}

async function decodeWithImageElement(file: File): Promise<DecodedPhoto> {
  if (typeof document === "undefined" || typeof URL.createObjectURL !== "function") {
    throw new FoodPhotoPreparationError("This browser cannot decode the selected photo", "decode-failed");
  }
  const objectUrl = URL.createObjectURL(file);
  const image = document.createElement("img");
  image.decoding = "async";
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("Image decode failed"));
      image.src = objectUrl;
    });
    const width = validDimension(image.naturalWidth);
    const height = validDimension(image.naturalHeight);
    if (!width || !height) throw new Error("Image has no pixels");
    return { source: image, width, height, dispose: () => URL.revokeObjectURL(objectUrl) };
  } catch {
    URL.revokeObjectURL(objectUrl);
    throw new FoodPhotoPreparationError("The selected photo could not be decoded", "decode-failed");
  } finally {
    image.onload = null;
    image.onerror = null;
  }
}

function encodeCappedJpeg(photo: DecodedPhoto, options: NormalizedFoodPhotoOptions): string {
  let { width, height } = fitWithin(photo.width, photo.height, options.maxDimension);
  for (let resizeAttempt = 0; resizeAttempt < RESIZE_ATTEMPTS; resizeAttempt += 1) {
    const canvas = createCanvas(width, height);
    let context: CanvasRenderingContext2D | null;
    try {
      context = canvas.getContext("2d", { alpha: false });
    } catch {
      throw new FoodPhotoPreparationError("Photo processing is unavailable in this browser", "canvas-unavailable");
    }
    if (!context) throw new FoodPhotoPreparationError("Photo processing is unavailable in this browser", "canvas-unavailable");
    try {
      context.drawImage(photo.source, 0, 0, width, height);
    } catch {
      throw new FoodPhotoPreparationError("The captured photo could not be processed", "encode-failed");
    }

    const initial = encodeJpeg(canvas, options.initialQuality);
    if (initial.bytes <= options.maxBytes) return initial.dataUrl;

    let lowerQuality = options.minimumQuality;
    let upperQuality = options.initialQuality;
    let smallest = encodeJpeg(canvas, lowerQuality);
    let best: EncodedJpeg | undefined = smallest.bytes <= options.maxBytes ? smallest : undefined;
    for (let step = 0; step < QUALITY_SEARCH_STEPS && best; step += 1) {
      const candidateQuality = (lowerQuality + upperQuality) / 2;
      const candidate = encodeJpeg(canvas, candidateQuality);
      if (candidate.bytes <= options.maxBytes) {
        best = candidate;
        lowerQuality = candidateQuality;
      } else {
        upperQuality = candidateQuality;
      }
    }
    if (best) return best.dataUrl;

    if (width === 1 && height === 1) break;
    const byteRatio = Math.sqrt(options.maxBytes / smallest.bytes) * 0.92;
    const scale = Math.max(0.25, Math.min(0.9, byteRatio));
    const nextWidth = Math.max(1, Math.floor(width * scale));
    const nextHeight = Math.max(1, Math.floor(height * scale));
    width = nextWidth === width && width > 1 ? width - 1 : nextWidth;
    height = nextHeight === height && height > 1 ? height - 1 : nextHeight;
  }
  throw new FoodPhotoPreparationError("The photo could not be reduced to the upload limit", "output-too-large");
}

interface EncodedJpeg {
  dataUrl: string;
  bytes: number;
}

function encodeJpeg(canvas: HTMLCanvasElement, qualityValue: number): EncodedJpeg {
  let dataUrl: string;
  try {
    dataUrl = canvas.toDataURL("image/jpeg", qualityValue);
  } catch {
    throw new FoodPhotoPreparationError("The photo could not be encoded", "encode-failed");
  }
  if (!/^data:image\/jpeg;base64,/i.test(dataUrl)) {
    throw new FoodPhotoPreparationError("JPEG encoding is unavailable in this browser", "encode-failed");
  }
  return { dataUrl, bytes: foodPhotoDataUrlByteLength(dataUrl) };
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  if (typeof document === "undefined") {
    throw new FoodPhotoPreparationError("Photo processing is unavailable in this browser", "canvas-unavailable");
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function fitWithin(width: number, height: number, maximum: number): { width: number; height: number } {
  const scale = Math.min(1, maximum / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function validDimension(value: number): number | undefined {
  return Number.isFinite(value) && value > 0 ? Math.floor(value) : undefined;
}
